import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useNoMatch, useReviewQueue, useSearch } from '../api/hooks.js';
import { Card } from '../components/Card.js';
import { cx, LINK, MONO } from '../components/classes.js';
import { EntityRow, EntityRowHead } from '../components/EntityRow.js';
import { FilterButtons, ToggleButton } from '../components/FilterButtons.js';
import { EmptyState, ProblemNote, SkeletonRows } from '../components/Notes.js';
import { useSearchShell } from '../components/SearchShell.js';
import { ApiError } from '../lib/api.js';
import { formatRead } from '../lib/format.js';
import type { LiveError } from '../lib/types.js';
import {
  applyFilters,
  joinCounts,
  useSearchStore,
  type KindFilter,
} from '../store/search.js';
import { canIssue, useSessionStore } from '../store/session.js';

/** The one loading line (owner instruction 2026-09-22): progress copy never names the registers. */
export const SEARCHING_COPY = 'Searching…';

type QueryKind = 'name' | 'tpin' | 'dira_id';

/** Same detection as the API: 10 digits = TPIN, `ZM-…` = Dira ID, else a name. */
export function queryKindOf(q: string): QueryKind {
  if (/^\d{10}$/.test(q)) return 'tpin';
  if (/^ZM-/i.test(q)) return 'dira_id';
  return 'name';
}

/** "Dira couldn't reach PACRA/ZRA just now — try again" — names only the registers that failed. */
export function liveErrorText(errors: readonly LiveError[]): string {
  const names = [...new Set(errors.map((e) => (e.source === 'pacra' ? 'PACRA' : 'ZRA')))];
  const who = names.length >= 2 ? 'PACRA/ZRA' : (names[0] ?? 'the registers');
  return `Dira couldn’t reach ${who} just now — try again`;
}

/**
 * Wireframe §4: the join-count line tells the truth up front; filters are the
 * register's vocabulary. The search field itself lives in the SearchShell
 * layout (it morphs from the home hero); this screen brings the count line
 * and filters up only once the shell says the dock transition `settled`.
 */
export function Results() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { settled } = useSearchShell();
  const urlQ = (params.get('q') ?? '').trim();
  const kind = useSearchStore((s) => s.kind);
  const activeOnly = useSearchStore((s) => s.activeOnly);
  const setKind = useSearchStore((s) => s.setKind);
  const toggleActiveOnly = useSearchStore((s) => s.toggleActiveOnly);
  const role = useSessionStore((s) => s.role);
  // The query the officer asked to read live; a new query goes back to the index first.
  const [liveQ, setLiveQ] = useState<string | null>(null);
  const live = liveQ !== null && liveQ === urlQ;

  const search = useSearch(urlQ, live);
  const review = useReviewQueue();
  const noMatch = useNoMatch();

  const loading = search.isPending && urlQ.length >= 2;

  const rows = search.data?.data ?? [];
  const meta = search.data?.meta;
  const queryKind = queryKindOf(urlQ);
  const searchedLive = meta?.searched_live === true;
  const liveErrors = meta?.live_errors ?? [];
  const liveFailed = liveErrors.length > 0;
  // "Not on the register" may only be said once both registers were actually read.
  const registersRead = searchedLive && !liveFailed;
  const canGoLive = queryKind !== 'dira_id' && search.data !== undefined;
  const counts = useMemo(() => joinCounts(rows), [rows]);
  const visible = useMemo(
    () => applyFilters(rows, { kind, activeOnly }),
    [rows, kind, activeOnly],
  );
  const reviewCounts = useMemo(() => {
    const m = new Map<string, number>();
    for (const item of review.data?.items ?? []) {
      m.set(item.entity.id, (m.get(item.entity.id) ?? 0) + item.candidates.length);
    }
    return m;
  }, [review.data]);

  const searchLive = () => {
    if (live) void search.refetch();
    else setLiveQ(urlQ);
  };

  const recordNoMatch = () => {
    noMatch.mutate(
      { name: urlQ },
      {
        onSuccess: (env) =>
          navigate(`/records/${env.data.id}`, { state: { issued: true } }),
      },
    );
  };

  const filters: Array<{ key: KindFilter; label: string }> = [
    { key: 'all', label: `All ${counts.register}` },
    { key: 'company', label: `Companies ${counts.byKind.company ?? 0}` },
    { key: 'business_name', label: `Business names ${counts.byKind.business_name ?? 0}` },
  ];
  const filteredOut = rows.length - visible.length;
  const noMatchCandidates =
    noMatch.error instanceof ApiError &&
    Array.isArray(noMatch.error.problem?.['candidates'])
      ? (noMatch.error.problem!['candidates'] as unknown[]).length
      : 0;

  const liveAction = (label: string) => (
    <button
      type="button"
      onClick={searchLive}
      disabled={!canGoLive || search.isFetching}
      className={cx(LINK, 'cursor-pointer disabled:cursor-default disabled:opacity-60')}
    >
      {label}
    </button>
  );

  const emptyBody = () => {
    if (rows.length > 0) {
      return (
        <EmptyState title="Nothing matches these filters">
          Clear a filter to see the other results.
        </EmptyState>
      );
    }
    if (registersRead) {
      return (
        <EmptyState title={`No company called “${urlQ}” on the register`}>
          PACRA and ZRA were both read just now. A company that is not on the register
          cannot be verified — treat this as a finding, not an error.
        </EmptyState>
      );
    }
    if (searchedLive) {
      return (
        <EmptyState title={`Nothing found yet for “${urlQ}”`}>
          A register could not be reached, so this is not a finding. Read the registers
          again before recording anything.
        </EmptyState>
      );
    }
    return (
      <EmptyState title={`Nothing in Dira’s index for “${urlQ}”`}>
        {queryKind === 'dira_id'
          ? 'A Dira ID only exists once a record has been issued — check the ID.'
          : 'The registers have not been read for this query.'}
      </EmptyState>
    );
  };

  return (
    <div className="flex flex-col gap-7">
      {/* Count line + filters arrive together, once the field has docked. */}
      <div className="flex min-h-9 items-center justify-between gap-6">
        {settled ? (
          <>
            <div
              className="animate-rise text-[14px] text-mute motion-reduce:animate-none"
              data-testid="join-counts"
            >
              {loading ? (
                SEARCHING_COPY
              ) : (
                <>
                  <b className="font-semibold text-ink">{counts.register}</b> on the
                  register · <b className="font-semibold text-ink">{counts.taxRecords}</b>{' '}
                  tax records · <b className="font-semibold text-ink">{counts.linked}</b>{' '}
                  linked
                  {meta?.data_as_of ? ` · ${formatRead(meta.data_as_of)}` : ''}
                  {meta
                    ? searchedLive
                      ? ' · read live just now'
                      : ' · from Dira’s index'
                    : ''}
                  {meta?.next_cursor ? ' · first 25 shown — narrow the search' : ''}
                </>
              )}
            </div>
            <div className="flex gap-2 animate-rise motion-reduce:animate-none">
              <FilterButtons
                options={filters}
                active={kind}
                onChange={setKind}
                label="Entity kind"
              />
              <ToggleButton active={activeOnly} onClick={toggleActiveOnly}>
                Active only
              </ToggleButton>
            </div>
          </>
        ) : null}
      </div>

      {search.isError ? <ProblemNote error={search.error} /> : null}
      {liveFailed ? (
        <ProblemNote
          error={new Error(liveErrorText(liveErrors))}
          action={liveAction('Try again')}
        />
      ) : null}

      <Card className="animate-fade motion-reduce:animate-none">
        <EntityRowHead />
        {loading ? (
          <SkeletonRows rows={4} stagger />
        ) : visible.length === 0 ? (
          <div className="border-t border-line">{emptyBody()}</div>
        ) : (
          visible.map((e, i) => (
            <EntityRow
              key={e.id}
              entity={e}
              reviewCount={reviewCounts.get(e.id) ?? 0}
              highlighted={i === 0 && kind === 'all' && !activeOnly}
            />
          ))
        )}
        <div className="flex flex-wrap items-center gap-x-2 border-t border-line px-6 py-3.5 text-[13px] text-mute">
          {filteredOut > 0 ? <span>{filteredOut} more behind the filters ·</span> : null}
          <span>Not here?</span>
          {registersRead ? (
            canIssue(role) ? (
              <button
                type="button"
                onClick={recordNoMatch}
                disabled={noMatch.isPending || !urlQ}
                className={cx(
                  LINK,
                  'cursor-pointer disabled:cursor-default disabled:opacity-60',
                )}
              >
                {noMatch.isPending ? 'Filing…' : `Record “no match” for “${urlQ}”`}
              </button>
            ) : (
              <span>A verifier can record “no match” for “{urlQ}”.</span>
            )
          ) : queryKind === 'dira_id' ? (
            <span>Search by name or TPIN to read the registers.</span>
          ) : (
            liveAction('Search the registers live')
          )}
          {queryKind === 'tpin' ? null : <span className={MONO}>· or try a TPIN</span>}
        </div>
      </Card>

      {noMatch.isError ? (
        <ProblemNote
          error={
            noMatchCandidates
              ? new Error(
                  `${noMatchCandidates} registered ${noMatchCandidates === 1 ? 'entity matches' : 'entities match'} “${urlQ}” — open one above and issue a verification instead.`,
                )
              : noMatch.error
          }
        />
      ) : null}
    </div>
  );
}
